import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";

import {
  AdditiveHeader,
  Button,
  EditableTextInput,
  Headline,
  Paragraph,
  Small,
} from "@fronterahealth/frontera-ui-components";

import { GoalDetailsPanel } from "@components/TreatmentPlan/GoalPanel/GoalDetailsPanel";
import { GoalItemType, TargetItem, goalWithDefaults, targetWithDefaults } from "@components/TreatmentPlan/utils";

export interface GoalExplorerProps {
  title: string;
  goalsList: GoalItemType[];
  setGoalsList: React.Dispatch<React.SetStateAction<GoalItemType[]>>;
}

export const GoalExplorer: React.FC<GoalExplorerProps> = ({ title, goalsList, setGoalsList }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(goalsList.length > 0 ? 0 : -1);

  // Validate selectedIndex when goalsList changes externally
  useEffect(() => {
    if (selectedIndex >= goalsList.length) {
      // If selected index is out of bounds, select the last item or null if empty
      setSelectedIndex(goalsList.length > 0 ? goalsList.length - 1 : -1);
    } else if (selectedIndex === -1 && goalsList.length > 0) {
      // If nothing selected but list has items, select first item
      setSelectedIndex(0);
    }
  }, [goalsList.length, selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!goalsList.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min((prev ?? -1) + 1, goalsList.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max((prev ?? -1) - 1, 0));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goalsList]);

  useEffect(() => {
    if (selectedIndex > -1) {
      const item = document.getElementById(`goal-item-${selectedIndex}`);
      item?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedIndex]);

  const handleAddNewGoal = () => {
    setGoalsList((prev) => [...prev, goalWithDefaults({ goalName: "new goal title" })]);
    setSelectedIndex(goalsList.length);
  };

  const handleGoalDataChange = (goalIndex: number, updatedFields: Partial<GoalItemType>) => {
    const updatedList = goalsList.map((g, i) => (i === goalIndex ? { ...g, ...updatedFields } : g));
    setGoalsList(updatedList);
  };

  const handleTargetChange = (goalIndex: number, targetIndex: number, updatedFields: Partial<TargetItem>) => {
    const updatedList = goalsList.map((g, i) => {
      if (i !== goalIndex) return g;
      const updatedTargets = g.targets.map((t, j) => (j === targetIndex ? { ...t, ...updatedFields } : t));
      return { ...g, targets: updatedTargets };
    });
    setGoalsList(updatedList);
  };

  const handleAddTarget = (goalIndex: number) => {
    const updatedList = goalsList.map((g, i) => {
      if (i !== goalIndex) return g;
      return {
        ...g,
        targets: [...g.targets, targetWithDefaults({ targetName: "New target" })],
      };
    });
    setGoalsList(updatedList);
  };

  const handleDeleteTarget = (goalIndex: number, targetIndex: number) => {
    const updatedList = goalsList.map((g, i) => {
      if (i !== goalIndex) return g;
      return {
        ...g,
        targets: g.targets.filter((_, idx) => idx !== targetIndex),
      };
    });
    setGoalsList(updatedList);
  };

  const handleDeleteGoal = (goalIndex: number) => {
    const updatedList = goalsList.filter((_, i) => i !== goalIndex);
    setGoalsList(updatedList);
    if (selectedIndex === -1) return;
    if (updatedList.length === 0) {
      setSelectedIndex(-1);
    } else {
      if (goalIndex === selectedIndex) {
        // Deleted the selected item - select the item now at this index, or the last item
        setSelectedIndex(Math.min(goalIndex, updatedList.length - 1));
      } else if (goalIndex < selectedIndex) {
        // Deleted before selected - shift selection down by 1
        setSelectedIndex(selectedIndex - 1);
      }
      // If deleted after selected, no change needed
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-[40vw] border-r  border-limestone-100 p flex flex-col h-full">
          {/* Sticky header */}
          <div className="shrink-0 bg-white sticky top-0 z-10 p-4">
            <AdditiveHeader onClick={handleAddNewGoal} type="h6" text={title ?? "Goals"} />
          </div>

          {/* Scrollable goals list */}
          <div className="min-h-[60vh] max-h-[60vh] overflow-y-auto">
            <div className="p-4 pt-0">
              {goalsList.length > 0 ? (
                goalsList.map((goal, index) => (
                  <div id={`goal-item-${index}`} key={`${goal.goalName}${index}`} className="flex w-full py-1">
                    <EditableTextInput
                      handleDelete={() => handleDeleteGoal(index)}
                      handleEdit={(newValue) => handleGoalDataChange(index, { goalName: newValue })}
                      onClick={() => setSelectedIndex(index)}
                      selected={index === selectedIndex}
                      name={goal.goalName}
                      extraContent={`(${goal.targets.length} targets)`}
                      isInputBordered
                      className="w-full"
                    />
                  </div>
                ))
              ) : (
                <div className="flex w-full min-h-[55vh] items-center justify-center flex-col p-4">
                  <Headline size="extra-small" colorType="primary">
                    No goals yet
                  </Headline>
                  <Paragraph size="medium">Start by adding a goal and building out its targets and details.</Paragraph>
                  <Button text="Add a goal" appearance="link" onClick={handleAddNewGoal} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-h-[69vh] min-h-[69vh] w-full overflow-y-auto p-4 bg-limestone-50 h-full">
          {goalsList[selectedIndex] && (
            <GoalDetailsPanel
              goal={goalsList[selectedIndex]}
              goalIndex={selectedIndex}
              defaultOpenState={true}
              handleAddTarget={handleAddTarget}
              handleTargetChange={handleTargetChange}
              handleDeleteTarget={handleDeleteTarget}
              handleGoalChange={handleGoalDataChange}
              handleDeleteGoal={handleDeleteGoal}
            />
          )}
        </div>
      </div>
      <div className="flex border-t p-4 gap-3 items-center">
        <Small>Scroll between goals</Small>
        <div className="flex gap-1 items-center">
          <div className=" border border-limestone-100 shadow-md p-2">
            <ArrowUpIcon className="h-4 w-4 text-text-secondary" />
          </div>
          <div className=" border border-limestone-100 shadow-md p-2">
            <ArrowDownIcon className="h-4 w-4  text-text-secondary" />
          </div>
        </div>
      </div>
    </div>
  );
};
